import { useEffect, useState } from 'react';
import useGoalProgress from '../hooks/useGoalProgress';
import { hasSeenGoalsSplash, markGoalsSplashSeen } from '../utils/goalsSplash';
import { monthLabel, overheadLabel, expectedToDate } from '../constants/goals';

const COPY = {
  en: {
    eyebrow: 'Season targets',
    title: (s) => `${s.label} Season Goals`,
    sub: (s) => `${monthLabel(s.startMonth)} – ${monthLabel(s.endMonth)} · ${overheadLabel(s.monthlyOverhead)} monthly overhead`,
    tours: 'Tours', travellers: 'Travellers',
    won: 'won', target: 'target', toGo: 'to go',
    seasonProgress: 'Season progress',
    enter: 'Enter portal',
    close: 'Close',
    notStarted: (s) => `Season starts ${monthLabel(s.startMonth)}`,
    monthsIn: (n, total) => `Month ${n} of ${total}`,
    onTrack: 'On track', behind: 'Behind', ahead: 'Ahead',
    footnote: 'Counts tours whose deposit is paid, in the month they depart.',
  },
  es: {
    eyebrow: 'Objetivos de temporada',
    title: (s) => `Temporada ${s.label}`,
    sub: (s) => `${monthLabel(s.startMonth)} – ${monthLabel(s.endMonth)} · ${overheadLabel(s.monthlyOverhead)} de gastos fijos al mes`,
    tours: 'Tours', travellers: 'Viajeros',
    won: 'conseguidos', target: 'objetivo', toGo: 'restantes',
    seasonProgress: 'Progreso de temporada',
    enter: 'Entrar al portal',
    close: 'Cerrar',
    notStarted: (s) => `La temporada empieza en ${monthLabel(s.startMonth)}`,
    monthsIn: (n, total) => `Mes ${n} de ${total}`,
    onTrack: 'En objetivo', behind: 'Por debajo', ahead: 'Por encima',
    footnote: 'Cuenta los tours con depósito pagado, en su mes de salida.',
  },
};

function pct(done, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

/** Big circular gauge — the hero number on each goal tile. */
function Gauge({ done, total, color, size = 108 }) {
  const stroke = 9;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = pct(done, total) / 100;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,.14)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color}
          strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - p)}
          style={{ transition: 'stroke-dashoffset 1s cubic-bezier(.4,0,.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-[26px] font-extrabold text-white tabular-nums">{done}</span>
        <span className="text-[12px] font-semibold text-white/45 tabular-nums mt-[3px]">/ {total}</span>
      </div>
    </div>
  );
}

function PaceTag({ done, expected, L }) {
  if (expected == null || expected <= 0) return null;
  const diff = done - expected;
  const tol = Math.max(0.5, expected * 0.1);
  const s = diff >= tol
    ? { bg: 'rgba(52,211,153,.18)', fg: '#6EE7B7', text: L.ahead }
    : diff <= -tol
      ? { bg: 'rgba(248,113,113,.18)', fg: '#FCA5A5', text: L.behind }
      : { bg: 'rgba(96,165,250,.18)', fg: '#93C5FD', text: L.onTrack };
  return (
    <span className="text-[10.5px] font-extrabold uppercase tracking-[0.6px] px-2 py-[3px] rounded-full"
      style={{ background: s.bg, color: s.fg }}>
      {s.text}
    </span>
  );
}

export default function GoalsSplash({ locale = 'en' }) {
  const L = COPY[locale] || COPY.en;
  const { goals, data, totals, elapsed, season, months, loading } = useGoalProgress();
  const [open, setOpen] = useState(() => !hasSeenGoalsSplash());

  // Escape closes it, and the body must not scroll behind the overlay.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function dismiss() {
    markGoalsSplashSeen();
    setOpen(false);
  }

  // Nothing to show if the viewer has no targets — don't flash an empty board
  // at people, and don't burn the once-per-session flag either.
  if (!open || loading || !goals.length) return null;

  const overallTours = pct(totals.tDone, totals.tTarget);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(6,14,28,.72)', backdropFilter: 'blur(6px)' }}
      role="dialog"
      aria-modal="true"
      aria-label={L.title(season)}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className="relative w-full max-w-[1080px] my-auto rounded-[20px] overflow-hidden shadow-2xl"
        style={{
          background: 'linear-gradient(150deg,#0D2240 0%,#10305C 46%,#0B1C34 100%)',
          animation: 'goalsSplashIn .38s cubic-bezier(.2,.8,.3,1) both',
        }}
      >
        <style>{`
          @keyframes goalsSplashIn {
            from { opacity: 0; transform: translateY(14px) scale(.985); }
            to   { opacity: 1; transform: none; }
          }
        `}</style>

        {/* Close */}
        <button
          onClick={dismiss}
          aria-label={L.close}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center transition"
          style={{ background: 'rgba(255,255,255,.10)', color: '#fff' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.20)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,.10)'; }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="px-8 pt-9 pb-7 sm:px-11">
          <div className="text-[11px] font-extrabold uppercase tracking-[1.6px] text-[#E8A020]">{L.eyebrow}</div>
          <h1 className="text-[30px] sm:text-[38px] font-extrabold text-white leading-[1.1] mt-2">{L.title(season)}</h1>
          <p className="text-[13.5px] text-white/55 mt-2">{L.sub(season)}</p>

          {/* Overall bar */}
          <div className="mt-6 max-w-[560px]">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.9px] text-white/45">
                {L.seasonProgress}
              </span>
              <span className="text-[12.5px] text-white/70 tabular-nums">
                <strong className="text-white font-extrabold">{totals.tDone}</strong> / {totals.tTarget} {L.tours.toLowerCase()}
                <span className="mx-2 text-white/25">·</span>
                <strong className="text-white font-extrabold">{totals.pDone}</strong> / {totals.pTarget} {L.travellers.toLowerCase()}
              </span>
            </div>
            <div className="h-[10px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,.12)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${overallTours}%`,
                  background: 'linear-gradient(90deg,#E8A020,#F5C563)',
                  transition: 'width 1s cubic-bezier(.4,0,.2,1)',
                }}
              />
            </div>
            <div className="text-[12px] text-white/45 mt-2">
              {elapsed === 0 ? L.notStarted(season) : L.monthsIn(elapsed, months.length)}
            </div>
          </div>
        </div>

        {/* Goal tiles */}
        <div className="px-8 sm:px-11 pb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {goals.map((g) => {
              const d = data[g.key] || { tours: 0, travellers: 0, toDate: { tours: 0 } };
              const exp = expectedToDate(g, elapsed);
              const toGo = Math.max(0, g.tours - d.tours);
              return (
                <div
                  key={g.key}
                  className="rounded-[14px] p-5 flex items-center gap-5"
                  style={{ background: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.09)' }}
                >
                  <Gauge done={d.tours} total={g.tours} color={g.color} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-[10px] h-[10px] rounded-full flex-shrink-0" style={{ background: g.color }} />
                      <span className="text-[16px] font-extrabold text-white truncate">{g.label}</span>
                      <PaceTag done={d.toDate?.tours ?? 0} expected={exp?.tours} L={L} />
                    </div>

                    <div className="mt-3 flex items-baseline gap-1.5">
                      <span className="text-[22px] font-extrabold text-white tabular-nums">{d.travellers}</span>
                      <span className="text-[13px] text-white/40 tabular-nums">/ {g.travellers}</span>
                      <span className="text-[12px] text-white/45 ml-1">{L.travellers.toLowerCase()}</span>
                    </div>
                    <div className="mt-2 h-[6px] rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,.12)' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct(d.travellers, g.travellers)}%`,
                          background: g.color,
                          transition: 'width 1s cubic-bezier(.4,0,.2,1)',
                        }}
                      />
                    </div>

                    <div className="text-[12px] text-white/45 mt-2.5">
                      {toGo > 0
                        ? <><strong className="text-white/75 font-bold">{toGo}</strong> {L.tours.toLowerCase()} {L.toGo}</>
                        : <span className="font-bold" style={{ color: '#6EE7B7' }}>✓ {L.target}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-8 sm:px-11 py-5 flex flex-wrap items-center justify-between gap-3"
          style={{ background: 'rgba(0,0,0,.22)', borderTop: '1px solid rgba(255,255,255,.07)' }}
        >
          <span className="text-[12px] text-white/40">{L.footnote}</span>
          <button
            onClick={dismiss}
            className="text-[13.5px] font-extrabold px-6 py-2.5 rounded-[10px] text-navy transition"
            style={{ background: '#E8A020' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#F5C563'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#E8A020'; }}
          >
            {L.enter} →
          </button>
        </div>
      </div>
    </div>
  );
}
